"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildHostScriptBagHelpers = buildHostScriptBagHelpers;
const EMPTY_BAG = {};
function escapeAttr(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}
function escapeJsDoubleQuotedString(s) {
    return String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
function readBag(player) {
    return player.hostScriptBag ?? EMPTY_BAG;
}
function optionLine(target, label, effects) {
    const raw = String(target);
    // Colons appear in JOIN_ADVENTURE:/START_ADVENTURE:… — quote as a literal attribute, not '''script'''.
    const quotedTarget = raw.includes(':') ? `"${raw.replace(/"/g, '')}"` : raw;
    const effectsAttr = effects ? `:effects='''${effects}'''` : '';
    return `<option:target=${quotedTarget}:display=block${effectsAttr}>${escapeAttr(label)}</option>`;
}
function pickerList(player, listKey) {
    const key = String(listKey || '').trim();
    if (!key) {
        return [];
    }
    return readBag(player).pickerLists?.[key] ?? [];
}
function findPickerItem(player, listKey, id) {
    const wanted = String(id || '').trim();
    if (!wanted) {
        return undefined;
    }
    return pickerList(player, listKey).find(item => item.id === wanted);
}
function pickerChildren(player, listKey, parentId) {
    return findPickerItem(player, listKey, parentId)?.children ?? [];
}
/**
 * Script APIs for host-warmed join/picker bags (see {@link Player.hostScriptBag}).
 */
function buildHostScriptBagHelpers(options) {
    const { player, getCurrentEntityId } = options;
    return {
        generateJoinAdventureOptions: (listKey) => {
            const key = String(listKey ?? '').trim();
            if (!key) {
                return '';
            }
            const lines = [];
            for (const row of readBag(player).joinOptions?.[key] ?? []) {
                const sessionId = String(row.sessionId || '').trim();
                const slot = String(row.playerSlot || 'next').trim() || 'next';
                if (!sessionId) {
                    continue;
                }
                const label = String(row.label || '').trim() || sessionId;
                lines.push(optionLine(`JOIN_ADVENTURE:${sessionId}:${slot}`, label));
            }
            return lines.join('\n');
        },
        generatePickerOptions: (listKey, listSceneId, variableName) => {
            const scene = String(listSceneId ?? '').trim();
            const varName = String(variableName ?? '').trim();
            if (!scene || !varName) {
                return '';
            }
            const lines = [];
            for (const item of pickerList(player, listKey ?? '')) {
                const id = String(item.id || '').trim();
                if (!id) {
                    continue;
                }
                const label = String(item.label || '').trim() || id;
                const effects = `setVariable("${escapeJsDoubleQuotedString(varName)}","${escapeJsDoubleQuotedString(id)}")`;
                lines.push(optionLine(scene, label, effects));
            }
            return lines.join('\n');
        },
        generatePickerChildOptions: (listKey, parentId, listSceneId, variableName) => {
            const scene = String(listSceneId ?? '').trim();
            const varName = String(variableName ?? '').trim();
            if (!scene || !varName) {
                return '';
            }
            const lines = [];
            for (const item of pickerChildren(player, listKey ?? '', parentId ?? '')) {
                const id = String(item.id || '').trim();
                if (!id) {
                    continue;
                }
                const label = String(item.label || '').trim() || id;
                const effects = `setVariable("${escapeJsDoubleQuotedString(varName)}","${escapeJsDoubleQuotedString(id)}")`;
                lines.push(optionLine(scene, label, effects));
            }
            return lines.join('\n');
        },
        getPickerChildCount: (listKey, parentId) => {
            return pickerChildren(player, listKey ?? '', parentId ?? '').length;
        },
        getPickerDefaultChildId: (listKey, parentId) => {
            const children = pickerChildren(player, listKey ?? '', parentId ?? '');
            if (children.length !== 1) {
                return '';
            }
            return String(children[0]?.id || '').trim();
        },
        getPickerItem: (listKey, id) => {
            return findPickerItem(player, listKey ?? '', id ?? '') ?? null;
        },
        getPickerChild: (listKey, parentId, childId) => {
            const wanted = String(childId || '').trim();
            if (!wanted) {
                return null;
            }
            return pickerChildren(player, listKey ?? '', parentId ?? '').find(item => item.id === wanted) ?? null;
        },
        getHostFlag: (name) => {
            const key = String(name || '').trim();
            if (!key) {
                return false;
            }
            return readBag(player).flags?.[key] === true;
        },
        getHostEntityStatus: (entityId) => {
            const id = String(entityId || getCurrentEntityId() || '').trim();
            if (!id) {
                return '';
            }
            return String(readBag(player).entityStatuses?.[id] ?? '');
        }
    };
}
//# sourceMappingURL=hostScriptBagHelpers.js.map